import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { OrderEntryOverStockWarning } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-over-stock-warning';
import {
  selectSalesPortalCartItem,
  useCartStore,
} from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { Typography } from '@usepepper/sprout-kit-ui';
import { View } from 'react-native';

interface OrderEntryOnHandCellProps {
  /** The on-hand quantity display value from price_edit_display_info. */
  displayValue: string | null;
  /** The raw numeric on-hand quantity for comparison with cart quantity. */
  onHandQuantity: number | null;
  variantPackUUID: string;
}

/**
 * Renders the On Hand column value with an inline over-stock warning icon
 * when the cart quantity exceeds the on-hand availability.
 *
 * @param props - {@link OrderEntryOnHandCellProps}
 */
export const OrderEntryOnHandCell = React.memo<OrderEntryOnHandCellProps>(
  ({ displayValue, onHandQuantity, variantPackUUID }) => {
    const [styles] = useStyles();

    // We subscribe individually to the cart store for an individual item
    //  to avoid re-renders to the whole table
    const cartQuantity = useCartStore(
      (state) =>
        selectSalesPortalCartItem(state, variantPackUUID)?.quantity ?? 0,
    );

    const overBy =
      onHandQuantity !== null && cartQuantity > onHandQuantity
        ? cartQuantity - onHandQuantity
        : 0;

    const isOverStock = overBy > 0;

    return (
      <View style={styles.container}>
        <Typography
          bold={isOverStock}
          color={isOverStock ? '$textWarning' : '$textPrimary'}
          size="$md"
        >
          {displayValue ?? ''}
        </Typography>
        {isOverStock ? <OrderEntryOverStockWarning overBy={overBy} /> : null}
      </View>
    );
  },
);

const useStyles = createStyles(() => ({
  container: {
    alignItems: 'center',
    flexDirection: 'row',
  },
}));
